import React, { useEffect, useRef } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { WebSocketProvider } from './context/WebSocketContext';
import { Toaster } from './components/ui/sonner';
import Layout from './components/Layout';
import AnnouncementDisplay from './components/AnnouncementDisplay';
import { useActivityTracker } from './hooks/useActivityTracker';
import LoginPage from './pages/LoginPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import DashboardPage from './pages/DashboardPage';
import OperationsDashboard from './pages/OperationsDashboard';
import UserManualPage from './pages/UserManualPage';
import InventoryPage from './pages/InventoryPage';
import CategoriesPage from './pages/CategoriesPage';
import LocationsPage from './pages/LocationsPage';
import ResponsiblesPage from './pages/ResponsiblesPage';
import MovementsPage from './pages/MovementsPage';
import ResponsivasPage from './pages/ResponsivasPage';
import EquipmentTrackingPage from './pages/EquipmentTrackingPage';
import CalendarPage from './pages/CalendarPage';
import ReportsPage from './pages/ReportsPage';
import UsersPage from './pages/UsersPage';
import PermissionsPage from './pages/PermissionsPage';
import AnnouncementsPage from './pages/AnnouncementsPage';
import AuditPage from './pages/AuditPage';
import ProfilePage from './pages/ProfilePage';
import StylePage from './pages/StylePage';
import BackupPage from './pages/BackupPage';
import EmailPage from './pages/EmailPage';
import FAQPage from './pages/FAQPage';
import DebuggerPage from './pages/DebuggerPage';
import OwnerAssignmentPage from './pages/OwnerAssignmentPage';
import NotificationsConfigPage from './pages/NotificationsConfigPage';
import TestingPage from './pages/TestingPage';
import ApiPage from './pages/ApiPage';
import TemplatesPage from './pages/TemplatesPage';
import VersionPage from './pages/VersionPage';
import DocumentacionPage from './pages/DocumentacionPage';
import EtiquetasPage from './pages/EtiquetasPage';
import OneDriveCallbackHandler from './pages/OneDriveCallbackHandler';
import AndroidPage from './pages/AndroidPage';
import MaintenancePage from './pages/MaintenancePage';
import ReservationsPage from './pages/ReservationsPage';
import ClientsPage from './pages/ClientsPage';
import ScheduledReportsPage from './pages/ScheduledReportsPage';
import SSOCallbackPage from './pages/SSOCallbackPage';
import SignMovementPage from './pages/SignMovementPage';
import EmailSchedulePage from './pages/EmailSchedulePage';
import CategoryMergePage from './pages/CategoryMergePage';
import './App.css';

// Smart root redirect - checks local token first to avoid blank page
const RootRedirect = () => {
  const token = localStorage.getItem('token');
  return <Navigate to={token ? "/dashboard" : "/login"} replace />;
};

// Apply saved theme mode on app load (before render to avoid flash)
const savedMode = localStorage.getItem('themeMode');
if (savedMode === 'light') {
  document.documentElement.classList.add('light');
} else {
  document.documentElement.classList.remove('light');
}

// Protected Route wrapper
const ProtectedRoute = ({ children, adminOnly = false, superAdminOnly = false, permission = null }) => {
  const { user, loading, hasPermission, isAdmin, isSuperAdmin } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="flex flex-col items-center">
          <img 
            src="https://cmidiomas.com/wp-content/uploads/2023/03/logo-cm-idiomas-blanco.png" 
            alt="CM Idiomas" 
            className="h-16 w-auto animate-pulse"
            onError={(e) => {
              e.target.style.display = 'none';
              e.target.nextSibling.style.display = 'flex';
            }}
          />
          <div className="hidden w-12 h-12 rounded-xl bg-gradient-to-br from-[#00B2A9] to-[#008079] animate-pulse" />
          <p className="mt-4 text-muted-foreground animate-pulse">Cargando...</p>
        </div>
      </div>
    );
  }
  
  if (!user) {
    return <Navigate to="/login" replace />;
  }

  // Check superAdminOnly first (most restrictive)
  if (superAdminOnly && !isSuperAdmin()) {
    return <Navigate to="/dashboard" replace />;
  }

  // Check specific permission if provided
  if (permission && !hasPermission(permission)) {
    // Even if adminOnly, permission takes precedence
    return <Navigate to="/dashboard" replace />;
  }

  // Check adminOnly only if no specific permission was provided
  if (adminOnly && !permission && !isAdmin()) {
    return <Navigate to="/dashboard" replace />;
  }
  
  return <Layout>{children}</Layout>;
};

// Public Route wrapper (redirects to dashboard if logged in)
const PublicRoute = ({ children }) => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="flex flex-col items-center">
          <img 
            src="https://cmidiomas.com/wp-content/uploads/2023/03/logo-cm-idiomas-blanco.png" 
            alt="CM Idiomas" 
            className="h-16 w-auto animate-pulse"
            onError={(e) => {
              e.target.style.display = 'none';
              e.target.nextSibling.style.display = 'flex';
            }}
          />
          <div className="hidden w-12 h-12 rounded-xl bg-gradient-to-br from-[#00B2A9] to-[#008079] animate-pulse" />
          <p className="mt-4 text-muted-foreground animate-pulse">Cargando...</p>
        </div>
      </div>
    );
  }
  
  if (user) {
    return <Navigate to="/dashboard" replace />;
  }
  
  return children;
};

function AppRoutes() {
  return (
    <Routes>
      {/* Public Routes */}
      <Route path="/login" element={
        <PublicRoute>
          <LoginPage />
        </PublicRoute>
      } />
      
      {/* Password Reset - Public */}
      <Route path="/reset-password" element={<ResetPasswordPage />} />
      
      {/* SSO Callback - Public, no redirect logic */}
      <Route path="/sso-callback" element={<SSOCallbackPage />} />

      {/* Public digital signature (QR target) - no login required */}
      <Route path="/firmar/:token" element={<SignMovementPage />} />

      {/* Protected Routes */}
      <Route path="/dashboard" element={
        <ProtectedRoute permission="view_dashboard">
          <DashboardPage />
        </ProtectedRoute>
      } />
      
      <Route path="/operations" element={
        <ProtectedRoute permission="view_operations">
          <OperationsDashboard />
        </ProtectedRoute>
      } />
      
      <Route path="/inventory" element={
        <ProtectedRoute permission="view_inventory">
          <InventoryPage />
        </ProtectedRoute>
      } />
      
      <Route path="/categories" element={
        <ProtectedRoute permission="view_categories">
          <CategoriesPage />
        </ProtectedRoute>
      } />
      
      <Route path="/locations" element={
        <ProtectedRoute permission="view_locations">
          <LocationsPage />
        </ProtectedRoute>
      } />
      
      <Route path="/responsibles" element={
        <ProtectedRoute permission="view_responsibles">
          <ResponsiblesPage />
        </ProtectedRoute>
      } />
      
      <Route path="/movements" element={
        <ProtectedRoute permission="view_movements">
          <MovementsPage />
        </ProtectedRoute>
      } />
      
      <Route path="/responsivas" element={
        <ProtectedRoute permission="view_responsivas">
          <ResponsivasPage />
        </ProtectedRoute>
      } />
      
      <Route path="/equipment-tracking" element={
        <ProtectedRoute permission="view_equipment_tracking">
          <EquipmentTrackingPage />
        </ProtectedRoute>
      } />
      
      <Route path="/calendar" element={
        <ProtectedRoute permission="view_calendar">
          <CalendarPage />
        </ProtectedRoute>
      } />
      
      <Route path="/reports" element={
        <ProtectedRoute permission="view_reports">
          <ReportsPage />
        </ProtectedRoute>
      } />
      
      <Route path="/maintenance" element={
        <ProtectedRoute permission="view_maintenance">
          <MaintenancePage />
        </ProtectedRoute>
      } />
      
      <Route path="/reservations" element={
        <ProtectedRoute permission="view_reservations">
          <ReservationsPage />
        </ProtectedRoute>
      } />

      <Route path="/clients" element={
        <ProtectedRoute permission="view_clients">
          <ClientsPage />
        </ProtectedRoute>
      } />
      
      <Route path="/scheduled-reports" element={
        <ProtectedRoute permission="view_reports">
          <ScheduledReportsPage />
        </ProtectedRoute>
      } />

      <Route path="/email-schedule" element={
        <ProtectedRoute superAdminOnly>
          <EmailSchedulePage />
        </ProtectedRoute>
      } />

      <Route path="/category-merge" element={
        <ProtectedRoute superAdminOnly>
          <CategoryMergePage />
        </ProtectedRoute>
      } />
      
      <Route path="/profile" element={
        <ProtectedRoute>
          <ProfilePage />
        </ProtectedRoute>
      } />
      
      <Route path="/faq" element={
        <ProtectedRoute>
          <FAQPage />
        </ProtectedRoute>
      } />

      <Route path="/templates" element={
        <ProtectedRoute permission="view_templates">
          <TemplatesPage />
        </ProtectedRoute>
      } />

      {/* Admin Only Routes */}
      <Route path="/users" element={
        <ProtectedRoute permission="view_users">
          <UsersPage />
        </ProtectedRoute>
      } />
      
      <Route path="/permissions" element={
        <ProtectedRoute superAdminOnly permission="manage_permissions">
          <PermissionsPage />
        </ProtectedRoute>
      } />
      
      <Route path="/audit" element={
        <ProtectedRoute permission="view_audit">
          <AuditPage />
        </ProtectedRoute>
      } />
      
      <Route path="/manual" element={
        <ProtectedRoute>
          <UserManualPage />
        </ProtectedRoute>
      } />
      
      <Route path="/style" element={
        <ProtectedRoute superAdminOnly permission="manage_style">
          <StylePage />
        </ProtectedRoute>
      } />
      
      <Route path="/backup" element={
        <ProtectedRoute superAdminOnly>
          <BackupPage />
        </ProtectedRoute>
      } />
      
      <Route path="/email" element={
        <ProtectedRoute superAdminOnly>
          <EmailPage />
        </ProtectedRoute>
      } />
      
      <Route path="/announcements" element={
        <ProtectedRoute permission="manage_announcements">
          <AnnouncementsPage />
        </ProtectedRoute>
      } />

      <Route path="/testeo" element={
        <ProtectedRoute adminOnly>
          <TestingPage />
        </ProtectedRoute>
      } />

      <Route path="/integraciones" element={
        <ProtectedRoute superAdminOnly>
          <ApiPage />
        </ProtectedRoute>
      } />

      <Route path="/debugger" element={
        <ProtectedRoute superAdminOnly>
          <DebuggerPage />
        </ProtectedRoute>
      } />

      <Route path="/owner-assignment" element={
        <ProtectedRoute adminOnly>
          <OwnerAssignmentPage />
        </ProtectedRoute>
      } />

      <Route path="/notifications" element={
        <ProtectedRoute adminOnly>
          <NotificationsConfigPage />
        </ProtectedRoute>
      } />

      <Route path="/version" element={
        <ProtectedRoute>
          <VersionPage />
        </ProtectedRoute>
      } />

      <Route path="/documentacion" element={
        <ProtectedRoute superAdminOnly>
          <DocumentacionPage />
        </ProtectedRoute>
      } />

      <Route path="/etiquetas" element={
        <ProtectedRoute permission="view_etiquetas">
          <EtiquetasPage />
        </ProtectedRoute>
      } />

      <Route path="/android" element={
        <ProtectedRoute superAdminOnly>
          <AndroidPage />
        </ProtectedRoute>
      } />

      {/* OneDrive OAuth Callback Handler - catches callback when it lands on frontend */}
      <Route path="/api/backup/onedrive/callback" element={<OneDriveCallbackHandler />} />

      {/* Redirects */}
      <Route path="/" element={<RootRedirect />} />
      <Route path="*" element={<Navigate to="/dashboard" replace />} />
    </Routes>
  );
}

// Componente que activa el tracking global de actividad
function ActivityTracker() {
  useActivityTracker();
  return null;
}

// Definición de temas disponibles con colores HSL
const THEMES = {
  'default': { 
    primary: '174 100% 35%',      // #00B2A9 - CM Idiomas turquesa
    secondary: '174 100% 25%',    // #008079
    accent: '199 89% 48%',        // #0ea5e9
    ring: '174 100% 35%'
  },
  'ocean': { 
    primary: '201 96% 32%',       // #0369a1
    secondary: '202 80% 24%',     // #0c4a6e
    accent: '199 89% 48%',        // #38bdf8
    ring: '201 96% 32%'
  },
  'forest': { 
    primary: '160 84% 39%',       // #059669
    secondary: '163 94% 16%',     // #064e3b
    accent: '160 67% 52%',        // #34d399
    ring: '160 84% 39%'
  }
};

// Componente que aplica el tema al iniciar
function ThemeInitializer() {
  useEffect(() => {
    const applyTheme = (themeId) => {
      const theme = THEMES[themeId];
      if (theme) {
        const root = document.documentElement;
        root.style.setProperty('--primary', theme.primary);
        root.style.setProperty('--primary-foreground', '0 0% 100%');
        root.style.setProperty('--accent', theme.accent);
        root.style.setProperty('--accent-foreground', '0 0% 100%');
        root.style.setProperty('--ring', theme.ring);
        root.style.setProperty('--sidebar-primary', theme.primary);
        root.style.setProperty('--sidebar-accent', theme.accent);
      }
    };
    
    // Apply saved theme immediately
    const savedTheme = localStorage.getItem('userTheme');
    if (savedTheme && THEMES[savedTheme]) {
      applyTheme(savedTheme);
    }
    
    // Listen for theme changes from other tabs/components
    const handleStorageChange = (e) => {
      if (e.key === 'userTheme' && e.newValue && THEMES[e.newValue]) {
        applyTheme(e.newValue);
      }
    };
    
    window.addEventListener('storage', handleStorageChange);
    
    // Also listen for custom theme change event
    const handleThemeChange = (e) => {
      if (e.detail && THEMES[e.detail]) {
        applyTheme(e.detail);
      }
    };
    window.addEventListener('themeChange', handleThemeChange);
    
    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('themeChange', handleThemeChange);
    };
  }, []);
  return null;
}

// Apply theme immediately on script load (before React mounts)
(function() {
  const THEME_VALUES = {
    'default': { primary: '174 100% 35%', accent: '199 89% 48%', ring: '174 100% 35%' },
    'ocean': { primary: '201 96% 32%', accent: '199 89% 48%', ring: '201 96% 32%' },
    'forest': { primary: '160 84% 39%', accent: '160 67% 52%', ring: '160 84% 39%' }
  };
  
  try {
    const savedTheme = localStorage.getItem('userTheme');
    if (savedTheme && THEME_VALUES[savedTheme]) {
      const theme = THEME_VALUES[savedTheme];
      document.documentElement.style.setProperty('--primary', theme.primary);
      document.documentElement.style.setProperty('--primary-foreground', '0 0% 100%');
      document.documentElement.style.setProperty('--ring', theme.ring);
    }
  } catch (e) {
    // Ignore localStorage errors
  }
})();

// Auto-refresh: polls /api/version and reloads if a new version is detected
function VersionChecker() {
  const initialVersion = useRef(null);
  const API_URL = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    let intervalId;

    const checkVersion = async () => {
      try {
        const res = await fetch(`${API_URL}/api/version`);
        if (!res.ok) return;
        const data = await res.json();
        const currentVersion = data.version;

        if (initialVersion.current === null) {
          initialVersion.current = currentVersion;
        } else if (initialVersion.current !== currentVersion) {
          console.log(`Nueva versión detectada: ${initialVersion.current} → ${currentVersion}. Recargando...`);
          window.location.reload(true);
        }
      } catch {
        // Silently ignore fetch errors
      }
    };

    checkVersion();
    intervalId = setInterval(checkVersion, 2 * 60 * 1000); // every 2 minutes
    return () => clearInterval(intervalId);
  }, [API_URL]);

  return null;
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <WebSocketProvider>
          <ThemeInitializer />
          <ActivityTracker />
          <VersionChecker />
          <AppRoutes />
          <AnnouncementDisplay />
          <Toaster 
            position="top-right"
            richColors
            closeButton
            toastOptions={{
              style: {
                background: 'hsl(var(--card))',
                border: '1px solid hsl(var(--border))',
                color: 'hsl(var(--foreground))'
              }
            }}
          />
        </WebSocketProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
